/**
 * Vera Academy - React Query hooks over the BFF client.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateGlossaryTerms } from '@shared/ui';
import * as api from './academy';

export const academyKeys = {
  all: ['academy'] as const,
  me: () => [...academyKeys.all, 'me'] as const,
  collections: (all: boolean) => [...academyKeys.all, 'collections', { all }] as const,
  docTypes: () => [...academyKeys.all, 'docTypes'] as const,
  content: (filter: api.ContentFilter) => [...academyKeys.all, 'content', filter] as const,
  contentTags: () => [...academyKeys.all, 'contentTags'] as const,
  article: (slug: string) => [...academyKeys.all, 'article', slug] as const,
  items: (filter: { kind?: api.ItemKind; status?: string; q?: string }) =>
    [...academyKeys.all, 'items', filter] as const,
  item: (id: string) => [...academyKeys.all, 'item', id] as const,
  terms: (all: boolean) => [...academyKeys.all, 'terms', { all }] as const,
};

export const useAcademyMe = () =>
  useQuery({ queryKey: academyKeys.me(), queryFn: api.fetchMe, staleTime: 5 * 60_000 });

export const useCollections = (all = false) =>
  useQuery({ queryKey: academyKeys.collections(all), queryFn: () => api.fetchCollections(all) });

/** The document-type vocabulary. Rarely changes, so it is cached for the hour. */
export const useDocTypes = () =>
  useQuery({ queryKey: academyKeys.docTypes(), queryFn: api.fetchDocTypes, staleTime: 60 * 60_000 });

export const usePublishedItems = (filter: api.ContentFilter) =>
  useQuery({ queryKey: academyKeys.content(filter), queryFn: () => api.fetchPublishedItems(filter) });

export const usePublishedTags = () =>
  useQuery({ queryKey: academyKeys.contentTags(), queryFn: api.fetchPublishedTags, staleTime: 60_000 });

export const usePublishedContent = (slug: string) =>
  useQuery({
    queryKey: academyKeys.article(slug),
    queryFn: () => api.fetchPublishedContent(slug),
    enabled: !!slug,
  });

export const useAuthorItems = (filter: { kind?: api.ItemKind; status?: string; q?: string }) =>
  useQuery({ queryKey: academyKeys.items(filter), queryFn: () => api.fetchAuthorItems(filter) });

export const useAuthorItem = (id: string) =>
  useQuery({ queryKey: academyKeys.item(id), queryFn: () => api.fetchAuthorItem(id), enabled: !!id });

export const useTerms = (all = false) =>
  useQuery({ queryKey: academyKeys.terms(all), queryFn: () => (all ? api.fetchAllTerms() : api.fetchTerms()) });

function useAcademyInvalidator() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: academyKeys.all });
}

export function useCreateCollection() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.createCollection, onSettled: invalidate });
}

/**
 * Free-entry collection resolution: authors type a collection NAME and this
 * turns it into an id, creating the collection on the fly when it does not
 * exist (and quietly reactivating a deactivated one of the same name).
 * Deliberately loose, per Sasha: a typo is just a collection you rename or
 * deactivate in the Collections dialog, not something to gate up front.
 */
export function useResolveCollectionId() {
  const collections = useCollections(true);
  const createCollection = useCreateCollection();
  const updateCollection = useUpdateCollection();
  return async (name: string): Promise<string | null> => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const all = collections.data ?? [];
    const match = all.find(
      (c) => c.title.toLowerCase() === trimmed.toLowerCase() || c.slug === api.slugify(trimmed)
    );
    if (match) {
      if (!match.isActive) {
        await updateCollection.mutateAsync({ id: match.id, patch: { isActive: true } });
      }
      return match.id;
    }
    const created = await createCollection.mutateAsync({
      slug: api.slugify(trimmed),
      title: trimmed,
      sortOrder: all.length,
    });
    return created.id;
  };
}

export function useUpdateCollection() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof api.updateCollection>[1] }) =>
      api.updateCollection(id, patch),
    onSettled: invalidate,
  });
}

export function useCreateItem() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.createItem, onSettled: invalidate });
}

export function useUpdateItem() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: api.ItemPatch }) => api.updateItem(id, patch),
    onSettled: invalidate,
  });
}

export function useDeleteItem() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.deleteItem, onSettled: invalidate });
}

export function useSaveBlocks() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({ itemId, blocks }: { itemId: string; blocks: api.EditorBlock[] }) =>
      api.saveBlocks(itemId, blocks),
    onSettled: invalidate,
  });
}

export function usePublishItem() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({ itemId, isMajor, changeNote }: { itemId: string; isMajor: boolean; changeNote?: string | null }) =>
      api.publishItem(itemId, { isMajor, changeNote }),
    onSettled: invalidate,
  });
}

/**
 * One document -> one proposed draft. Deliberately NOT a query: it is a paid
 * model call per invocation, so it only ever runs when the author asks.
 */
export function useImportDocument() {
  return useMutation({ mutationFn: api.importDocument });
}

export function useCreateTerm() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: api.createTerm,
    onSettled: () => {
      invalidateGlossaryTerms();
      invalidate();
    },
  });
}

export function useUpdateTerm() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<api.TermInput> }) => api.updateTerm(id, patch),
    onSettled: () => {
      invalidateGlossaryTerms();
      invalidate();
    },
  });
}

export function useDeleteTerm() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: api.deleteTerm,
    onSettled: () => {
      invalidateGlossaryTerms();
      invalidate();
    },
  });
}

// ---------------------------------------------------------------------------
// Learn hooks
// ---------------------------------------------------------------------------

export const learnKeys = {
  catalog: () => [...academyKeys.all, 'learn', 'catalog'] as const,
  course: (slug: string) => [...academyKeys.all, 'learn', 'course', slug] as const,
  lesson: (courseSlug: string, itemSlug: string) =>
    [...academyKeys.all, 'learn', 'lesson', courseSlug, itemSlug] as const,
  authorCourses: () => [...academyKeys.all, 'authorCourses'] as const,
  authorCourse: (id: string) => [...academyKeys.all, 'authorCourse', id] as const,
  paths: () => [...academyKeys.all, 'paths'] as const,
};

export const useCatalog = () => useQuery({ queryKey: learnKeys.catalog(), queryFn: api.fetchCatalog });

export const useLearnCourse = (slug: string) =>
  useQuery({ queryKey: learnKeys.course(slug), queryFn: () => api.fetchLearnCourse(slug), enabled: !!slug });

export const useLesson = (courseSlug: string, itemSlug: string) =>
  useQuery({
    queryKey: learnKeys.lesson(courseSlug, itemSlug),
    queryFn: () => api.fetchLesson(courseSlug, itemSlug),
    enabled: !!courseSlug && !!itemSlug,
  });

export function useEnroll() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.enrollInCourse, onSettled: invalidate });
}

export function useMarkLesson() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({ courseSlug, itemSlug, complete }: { courseSlug: string; itemSlug: string; complete: boolean }) =>
      api.markLessonProgress(courseSlug, itemSlug, complete),
    onSettled: invalidate,
  });
}

export const useAuthorCourses = () =>
  useQuery({ queryKey: learnKeys.authorCourses(), queryFn: api.fetchAuthorCourses });

export const useAuthorCourse = (id: string) =>
  useQuery({ queryKey: learnKeys.authorCourse(id), queryFn: () => api.fetchAuthorCourse(id), enabled: !!id });

export function useCreateCourse() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.createCourse, onSettled: invalidate });
}

export function useUpdateCourse() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof api.updateCourse>[1] }) =>
      api.updateCourse(id, patch),
    onSettled: invalidate,
  });
}

export function useDeleteCourse() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.deleteCourse, onSettled: invalidate });
}

export function useSetCourseLessons() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({ id, lessons }: { id: string; lessons: { itemId: string; isRequired: boolean }[] }) =>
      api.setCourseLessons(id, lessons),
    onSettled: invalidate,
  });
}

export function usePublishCourse() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({ id, isMajor }: { id: string; isMajor: boolean }) => api.publishCourse(id, isMajor),
    onSettled: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Assessment hooks (quiz authoring + learner flow + certificates)
// ---------------------------------------------------------------------------

export const assessKeys = {
  courseAssessment: (courseId: string) => [...academyKeys.all, 'courseAssessment', courseId] as const,
  quizStatus: (courseSlug: string) => [...academyKeys.all, 'quizStatus', courseSlug] as const,
  myCertificates: () => [...academyKeys.all, 'myCertificates'] as const,
  certificate: (serial: string) => [...academyKeys.all, 'certificate', serial] as const,
  courseCertificate: (courseSlug: string) => [...academyKeys.all, 'courseCertificate', courseSlug] as const,
};

export const useCourseAssessment = (courseId: string) =>
  useQuery({
    queryKey: assessKeys.courseAssessment(courseId),
    queryFn: () => api.fetchCourseAssessment(courseId),
    enabled: !!courseId,
  });

export function useUpsertCourseAssessment() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({ courseId, input }: { courseId: string; input: api.AssessmentInput }) =>
      api.upsertCourseAssessment(courseId, input),
    onSettled: invalidate,
  });
}

export function useSaveAssessmentQuestions() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({
      assessmentId,
      questions,
    }: {
      assessmentId: string;
      questions: Parameters<typeof api.saveAssessmentQuestions>[1];
    }) => api.saveAssessmentQuestions(assessmentId, questions),
    onSettled: invalidate,
  });
}

export function useDeleteAssessment() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.deleteAssessment, onSettled: invalidate });
}

export const useQuizStatus = (courseSlug: string) =>
  useQuery({
    queryKey: assessKeys.quizStatus(courseSlug),
    queryFn: () => api.fetchQuizStatus(courseSlug),
    enabled: !!courseSlug,
  });

export function useStartQuiz() {
  return useMutation({ mutationFn: api.startQuizAttempt });
}

export function useSubmitQuiz() {
  const invalidate = useAcademyInvalidator();
  return useMutation({
    mutationFn: ({
      attemptId,
      answers,
    }: {
      attemptId: string;
      answers: { questionId: string; selectedOptionIds?: string[]; textValue?: string }[];
    }) => api.submitQuizAttempt(attemptId, answers),
    onSettled: invalidate,
  });
}

export const useMyCertificates = () =>
  useQuery({ queryKey: assessKeys.myCertificates(), queryFn: api.fetchMyCertificates });

export const useCertificate = (serial: string) =>
  useQuery({
    queryKey: assessKeys.certificate(serial),
    queryFn: () => api.fetchCertificate(serial),
    enabled: !!serial,
  });

export const useCourseCertificate = (courseSlug: string, enabled: boolean) =>
  useQuery({
    queryKey: assessKeys.courseCertificate(courseSlug),
    queryFn: () => api.fetchCourseCertificate(courseSlug),
    enabled: !!courseSlug && enabled,
  });

// ---------------------------------------------------------------------------
// Admin: quiz reset
// ---------------------------------------------------------------------------

export const useQuizResetTarget = (courseSlug: string, personEntraId: string, enabled: boolean) =>
  useQuery({
    queryKey: [...academyKeys.all, 'quizResetTarget', courseSlug, personEntraId] as const,
    queryFn: () => api.fetchQuizResetTarget(courseSlug, personEntraId),
    enabled: enabled && !!courseSlug && !!personEntraId,
  });

export const useQuizResets = (filter: { course?: string; person?: string; limit?: number }, enabled: boolean) =>
  useQuery({
    queryKey: [...academyKeys.all, 'quizResets', filter] as const,
    queryFn: () => api.fetchQuizResets(filter),
    enabled,
  });

export function useResetQuiz() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.resetQuiz, onSettled: invalidate });
}

export const useReportSummary = () =>
  useQuery({ queryKey: [...academyKeys.all, 'reportSummary'], queryFn: api.fetchReportSummary });

export const useReportEnrollments = (courseSlug?: string) =>
  useQuery({
    queryKey: [...academyKeys.all, 'reportEnrollments', courseSlug ?? ''],
    queryFn: () => api.fetchReportEnrollments(courseSlug),
  });

// ---------------------------------------------------------------------------
// Assignment (Phase 4a)
// ---------------------------------------------------------------------------

export const useMyAssignments = () =>
  useQuery({
    queryKey: [...academyKeys.all, 'myAssignments'],
    queryFn: api.fetchMyAssignments,
  });

/**
 * The nav/tile badge count. Polled rather than invalidated from everywhere:
 * completing a course closes an assignment deep inside the BFF completion
 * authority, so the client cannot reliably know when to refetch.
 */
export const useMyAssignmentCount = () =>
  useQuery({
    queryKey: [...academyKeys.all, 'myAssignmentCount'],
    queryFn: api.fetchMyAssignmentCount,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });

export const useAssignments = (courseSlug?: string, enabled = true) =>
  useQuery({
    queryKey: [...academyKeys.all, 'assignments', courseSlug ?? ''],
    queryFn: () => api.fetchAssignments(courseSlug),
    enabled,
  });

export const useResolveAssignmentCourses = (
  courseSlugs: string[],
  categories: string[],
  enabled: boolean
) =>
  useQuery({
    queryKey: [...academyKeys.all, 'resolveCourses', courseSlugs.join(','), categories.join(',')],
    queryFn: () => api.resolveAssignmentCourses(courseSlugs, categories),
    enabled: enabled && (courseSlugs.length > 0 || categories.length > 0),
  });

export function useCreateAssignments() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.createAssignments, onSettled: invalidate });
}

export function useWaiveAssignment() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.waiveAssignment, onSettled: invalidate });
}

export function useRevokeAssignment() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.revokeAssignment, onSettled: invalidate });
}

export function useRunAssignmentSweeps() {
  const invalidate = useAcademyInvalidator();
  return useMutation({ mutationFn: api.runAssignmentSweeps, onSettled: invalidate });
}
